/**
 * axiosCache.js — install once, helps every page.
 *
 * Two layers of optimisation on top of the global `axios` instance:
 *
 *  1) IN-FLIGHT DEDUPE — when 5 components mount in the same tick and all
 *     request `/api/clients/`, only ONE HTTP call goes out. The other 4
 *     await the same Promise. Zero staleness risk; pure savings.
 *
 *  2) MICRO-TTL CACHE (5 seconds, GET only) — if you navigate Dashboard →
 *     Sales → Dashboard within 5 seconds, the second Dashboard load uses
 *     the cached response instantly. After 5s, fresh data is fetched.
 *     5s is the sweet spot: short enough that mutations propagate quickly,
 *     long enough that operator navigation feels instant.
 *
 * The cache is invalidated automatically when the same URL is hit with
 * a non-GET method (POST/PUT/DELETE) — so creating a bill auto-invalidates
 * the bill-log cache.
 *
 * Bypass: pass `{ bypassCache: true }` in the request config when you
 * genuinely need fresh data (e.g. after a long-running background job).
 */
import axios from 'axios';
// Modern axios doesn't expose its built-in adapters on `axios.defaults.adapter`
// (it's undefined until an interceptor sets it). To safely wrap the network
// layer we grab the right adapter for the current environment manually.
// The browser bundle exposes `xhr`; node would use `http`. We stick to `xhr`.
// eslint-disable-next-line
const origAdapter = axios.getAdapter ? axios.getAdapter('xhr') : null;

const cache = new Map();           // url -> { ts, response }
const inFlight = new Map();        // url -> Promise
const TTL_MS = 5000;

// Helper — derive cache key
const keyOf = (config) => {
  const url = (config.baseURL || '') + (config.url || '');
  const params = config.params ? JSON.stringify(config.params) : '';
  return url + params;
};

// Wrap the default adapter
// (origAdapter is defined above via axios.getAdapter('xhr'))

axios.defaults.adapter = async (config) => {
  // If we couldn't grab the original adapter (older axios?), fall back to
  // letting axios pick — by deleting the override the request will use
  // axios' built-in default.
  if (!origAdapter) {
    delete axios.defaults.adapter;
    return axios(config);
  }
  const method = (config.method || 'get').toLowerCase();
  const key = keyOf(config);

  // Invalidate cache on mutations — POST/PUT/PATCH/DELETE on /resource/
  // also clears /resource and /resource/* entries.
  if (method !== 'get') {
    const baseUrl = (config.baseURL || '') + (config.url || '').split('?')[0];
    for (const k of cache.keys()) {
      // Crude but effective — invalidate any cached key that shares the
      // base resource path. e.g. POST /api/vouchers/ invalidates
      // GET /api/vouchers/?date_from=… and GET /api/vouchers/summary.
      const resource = baseUrl.replace(/\/[^/]*$/, '');
      if (k.startsWith(resource)) cache.delete(k);
    }
    return origAdapter(config);
  }

  if (config.bypassCache) {
    cache.delete(key);
    return origAdapter(config);
  }

  // 5-second cache hit
  const hit = cache.get(key);
  if (hit && Date.now() - hit.ts < TTL_MS) {
    return Promise.resolve(hit.response);
  }

  // In-flight dedupe
  if (inFlight.has(key)) return inFlight.get(key);

  const p = origAdapter(config)
    .then(resp => {
      // Cache only successful 2xx
      if (resp.status >= 200 && resp.status < 300) {
        cache.set(key, { ts: Date.now(), response: resp });
      }
      return resp;
    })
    .finally(() => inFlight.delete(key));

  inFlight.set(key, p);
  return p;
};

// Expose helpers for explicit invalidation (rarely needed)
export const clearApiCache = () => { cache.clear(); inFlight.clear(); };
export const invalidateUrl = (urlSubstring) => {
  for (const k of cache.keys()) {
    if (k.includes(urlSubstring)) cache.delete(k);
  }
};
